/**
 * RH-MENU — os textos de tela (R6 do pedido, LITERAIS) num lugar só.
 *
 * 🔴 Regra 10 do pedido: texto de tela que não está na tabela R6 → parar e
 * perguntar. Por isso tudo o que o RH escreve para o usuário sai daqui, e as
 * chaves têm o MESMO nome da tabela (o servidor responde erro com elas:
 * `cpf_ja_existe`, `erro_mover_departamento_lider`…).
 *
 * Única exceção decidida pelo Matheus (03/10): o departamento obrigatório no
 * cadastro novo usa a mensagem padrão de campo obrigatório do sistema.
 */

export const RH = {
  menu: 'RH',
  permissao_rh: 'Cuida do RH',
  permissao_rh_ajuda:
    'Vê e edita o cadastro de todos os funcionários no RH, inclusive salário. Não dá acesso a testes nem a feedback.',
  titulo_lista: 'Funcionários',
  titulo_novo: 'Novo funcionário',
  botao_novo: 'Novo funcionário',
  vazio: 'Nenhum funcionário cadastrado.',
  vazio_lider: 'Nenhum funcionário no seu departamento.',
  // Decisão do Matheus (03/10, depois da 1ª entrega): o vazio COM busca/filtro.
  vazio_filtro: 'Nenhum funcionário encontrado com esses filtros.',
  // Decisão do Matheus (03/10): o vazio das Observações internas na ficha do RH.
  observacoes_vazio: 'Use pra registrar combinados sobre o funcionário que quem cuida do RH precisa ver.',
  busca_placeholder: 'Buscar por nome ou documento',
  col_nome: 'Nome',
  col_cargo: 'Cargo',
  col_departamento: 'Departamento',
  col_telefone: 'Telefone',
  col_admissao: 'Admissão',
  col_situacao: 'Situação',
  filtro_departamento: 'Departamento',
  filtro_situacao: 'Situação',
  filtro_todos: 'Todos',
  situacao_ativo: 'Ativo',
  situacao_desligado: 'Desligado',
  situacao_inativo: 'Cadastro inativo',
  secao_rh: 'Dados de RH',
  campo_cargo: 'Cargo',
  // FEEDBACK-CARREIRA, PARTE B (05/10/2026) — literais do pedido.
  campo_area: 'Área',
  campo_gerencia: 'Gerência',
  filtro_area: 'Área',
  cargo_anterior_texto: 'Cargo anterior (texto)',
  cargo_escolha_area_antes: 'Escolha a área primeiro.',
  campo_admissao: 'Data de admissão',
  campo_desligamento: 'Data de desligamento',
  campo_nascimento: 'Data de nascimento',
  campo_clt: 'Registro CLT',
  campo_cbo: 'CBO',
  campo_salario: 'Salário',
  campo_tempo_casa: 'Tempo de casa',
  tempo_casa_desligado: 'Tempo de casa até o desligamento',
  campo_departamento: 'Departamento',
  campo_departamento_ajuda: 'Usado só para quem não tem login no sistema.',
  campo_login: 'Login do funcionário',
  campo_login_ajuda: 'Escolha o login desta pessoa. A escolha feita aqui não é trocada automaticamente.',
  campo_documento: 'CPF (ou CNPJ, se for MEI)',
  documento_obrigatorio: 'Informe o CPF (ou o CNPJ, se for MEI) para salvar.',
  documento_invalido: 'Documento inválido. Confira os números.',
  documento_pendente: 'Documento pendente. Peça ao financeiro para completar no Omie.',
  cpf_ja_existe: 'Já existe um funcionário com este documento.',
  cpf_ja_existe_link: 'Abrir ficha',
  cpf_fale_diretoria: 'Este documento já está cadastrado no sistema. Fale com a diretoria.',
  cpf_e_cliente:
    'Este documento já está cadastrado como cliente ou fornecedor. Transformar esse cadastro em funcionário?',
  botao_transformar: 'Transformar em funcionário',
  confirmar_transformar:
    'Transformar este cadastro em funcionário? Ele sai de Clientes e passa a aparecer só no RH.',
  confirmar_remover_etiqueta:
    'Tirar a etiqueta de funcionário? Cargo, datas, registro CLT, CBO e departamento serão apagados e a ficha volta para Clientes. Não é possível desfazer.',
  confirmar_reativacao: 'Desfazer o desligamento? A pessoa volta a aparecer como Ativa no RH.',
  erro_departamento_lider: 'Você só pode cadastrar funcionários do seu departamento.',
  erro_mover_departamento_lider: 'Você só pode mover funcionários para o seu departamento.',
  campo_restrita: 'Ficha restrita à diretoria',
  campo_restrita_ajuda: 'Só a diretoria vê esta ficha, mesmo sem login vinculado.',
  opcao_sem_login: 'Sem login',
  botao_desfazer_vinculo: 'Desfazer vínculo',
  vinculo_automatico: 'Ligado automaticamente pelo nome. Confira se é a pessoa certa.',
  aviso_tem_vinculos: 'Este cadastro tem OS, contrato ou orçamento ligado.',
  erro_nome_outro_departamento: 'Esta pessoa tem login em outro departamento. Fale com a diretoria.',
  aviso_sem_departamento: 'Esta ficha não tem departamento. Até ele ser definido, nenhum líder vê este funcionário.',
  aviso_login_sem_departamento:
    'O login desta pessoa não tem departamento. Defina em Usuários. Até lá, nenhum líder vê este funcionário.',
  sem_omie: 'Este funcionário ainda não está no Omie. O cadastro no Omie é feito pelo financeiro.',
  nao_encontrado: 'Cadastro não encontrado.',
} as const;

/** "dd/mm/aaaa" a partir de "aaaa-mm-dd" (data pura, sem fuso). */
export function dataBR(ymd: string | null | undefined): string {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return '';
  const [a, m, d] = ymd.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

export const desligamentoAgendadoTexto = (ymd: string) => `Desligamento em ${dataBR(ymd)}`;
export const confirmarDesligamentoTexto = (ymd: string) =>
  `Confirmar o desligamento em ${dataBR(ymd)}? A partir dessa data, a pessoa aparece como Desligada no RH.`;
export const transformarBloqueadoTexto = (lista: string) =>
  `Não é possível transformar em funcionário: este cadastro tem itens ligados. Encerre ou transfira antes: ${lista}`;

/**
 * Tempo de casa (R6): "{anos} anos e {meses} meses" · "1 ano e {meses} meses" ·
 * "{meses} meses" ("1 mês" no singular). O singular vale nas três formas
 * (decisão própria: "1 ano e 1 meses" seria erro de português na tela).
 */
export function tempoDeCasaTexto(t: { anos: number; meses: number }): string {
  const meses = t.meses === 1 ? '1 mês' : `${t.meses} meses`;
  if (t.anos === 0) return meses;
  if (t.anos === 1) return `1 ano e ${meses}`;
  return `${t.anos} anos e ${meses}`;
}

export type SituacaoRh = 'ativo' | 'desligado' | 'inativo';

export const ROTULO_SITUACAO: Record<SituacaoRh, string> = {
  ativo: RH.situacao_ativo,
  desligado: RH.situacao_desligado,
  inativo: RH.situacao_inativo,
};

/** Erro do servidor (chave R6) → texto da tela. Chave desconhecida → null (quem chama usa o genérico). */
export function textoDoErroRh(chave: string | null | undefined): string | null {
  if (!chave) return null;
  const mapa: Record<string, string> = {
    documento_obrigatorio: RH.documento_obrigatorio,
    documento_invalido: RH.documento_invalido,
    cpf_ja_existe: RH.cpf_ja_existe,
    cpf_fale_diretoria: RH.cpf_fale_diretoria,
    cpf_e_cliente: RH.cpf_e_cliente,
    erro_departamento_lider: RH.erro_departamento_lider,
    erro_mover_departamento_lider: RH.erro_mover_departamento_lider,
    erro_nome_outro_departamento: RH.erro_nome_outro_departamento,
    'nao-encontrado': RH.nao_encontrado,
  };
  return mapa[chave] ?? null;
}
